import type { Locale } from '@iac/contracts';
import { useState } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { LineListEditor } from './LineListEditor';

const sample: Dictionary<{
  ingredients: string;
  steps: string;
  ingredient: (n: number) => string;
  step: (n: number) => string;
  moveUp: string;
  moveDown: string;
  remove: string;
  addIngredient: string;
  addStep: string;
  ingredientLines: string[];
  stepLines: string[];
  rowIds: string;
  newRow: string;
}> = {
  pl: {
    ingredients: 'Składniki',
    steps: 'Przygotowanie',
    ingredient: (n) => `Składnik ${n}`,
    step: (n) => `Krok ${n}`,
    moveUp: 'Przesuń w górę',
    moveDown: 'Przesuń w dół',
    remove: 'Usuń',
    addIngredient: 'Dodaj składnik',
    addStep: 'Dodaj krok',
    ingredientLines: [
      '500 ml zakwasu',
      '200 g białej kiełbasy',
      '2 ząbki czosnku',
    ],
    stepLines: [
      'Kiełbasę zalej litrem wody i gotuj 20 minut.',
      'Wyjmij kiełbasę, do wywaru wlej zakwas i dodaj przeciśnięty czosnek. Gotuj na małym ogniu jeszcze 10 minut, aż zupa lekko zgęstnieje.',
    ],
    rowIds: 'Identyfikatory wierszy',
    newRow: 'nowy',
  },
  en: {
    ingredients: 'Ingredients',
    steps: 'Method',
    ingredient: (n) => `Ingredient ${n}`,
    step: (n) => `Step ${n}`,
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    addIngredient: 'Add ingredient',
    addStep: 'Add step',
    ingredientLines: [
      '500 ml sourdough starter',
      '200 g white sausage',
      '2 garlic cloves',
    ],
    stepLines: [
      'Cover the sausage with a litre of water and simmer for 20 minutes.',
      'Take the sausage out, pour the starter into the broth and add the pressed garlic. Simmer for another 10 minutes until the soup thickens slightly.',
    ],
    rowIds: 'Row ids',
    newRow: 'new',
  },
};

/** The editor owns no lines; the form around it does. */
function Demo({ locale, numbered }: { locale: Locale; numbered: boolean }) {
  const t = sample[locale];
  const [lines, setLines] = useState(
    numbered ? t.stepLines : t.ingredientLines,
  );
  return (
    <LineListEditor
      label={numbered ? t.steps : t.ingredients}
      value={lines}
      onChange={setLines}
      numbered={numbered}
      labels={{
        item: (i) => (numbered ? t.step(i + 1) : t.ingredient(i + 1)),
        moveUp: t.moveUp,
        moveDown: t.moveDown,
        remove: t.remove,
        add: numbered ? t.addStep : t.addIngredient,
      }}
    />
  );
}

/**
 * Rows handed in with ids, and the ids read back after every change — what a
 * form uses to tell an edited row from a new one.
 */
function WithIdsDemo({ locale }: { locale: Locale }) {
  const t = sample[locale];
  const [lines, setLines] = useState(t.ingredientLines);
  const [ids, setIds] = useState<(string | null)[]>(() =>
    t.ingredientLines.map((_, i) => `id-${i + 1}`),
  );
  return (
    <div className="form-stack">
      <LineListEditor
        label={t.ingredients}
        value={lines}
        ids={ids}
        onChange={(nextLines, nextIds) => {
          setLines(nextLines);
          setIds(nextIds);
        }}
        labels={{
          item: (i) => t.ingredient(i + 1),
          moveUp: t.moveUp,
          moveDown: t.moveDown,
          remove: t.remove,
          add: t.addIngredient,
        }}
      />
      <p className="text-hint">
        {t.rowIds}: {ids.map((id) => id ?? t.newRow).join(', ')}
      </p>
    </div>
  );
}

export default {
  kind: 'component',
  title: { pl: 'Edytor listy linii', en: 'Line list editor' },
  description: {
    pl: 'Uporządkowana lista jednowierszowych pozycji, np. składników albo kroków. Enter dzieli wiersz w miejscu kursora, Backspace w pustym wierszu go usuwa, a wklejenie kilku linii tworzy wiersz na każdą z nich. Wiersze rosną razem z tekstem, a strzałki zmieniają kolejność. Wariant `numbered` numeruje wiersze od 1. Z `ids` każdy wiersz zachowuje swój identyfikator przy pisaniu i przesuwaniu, przy podziale zostaje on przy pierwszej części, a nowe wiersze nie mają żadnego.',
    en: 'An ordered list of one-line items, such as ingredients or steps. Enter splits a row at the caret, Backspace in an empty row removes it, and pasting several lines makes a row for each. Rows grow with their text, and the arrows reorder them. The `numbered` variant numbers rows from 1. With `ids`, each row keeps its id through typing and moves, the first part of a split keeps it, and new rows report none.',
  },
  variants: [
    {
      name: { pl: 'Zwykła', en: 'Plain' },
      render: (locale) => <Demo locale={locale} numbered={false} />,
    },
    {
      name: { pl: 'Numerowana', en: 'Numbered' },
      render: (locale) => <Demo locale={locale} numbered />,
    },
    {
      name: { pl: 'Z identyfikatorami', en: 'With ids' },
      render: (locale) => <WithIdsDemo locale={locale} />,
    },
  ],
} satisfies Story;
